import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MembershipRole, PendingInvite, WorkspaceMember, WorkspacePlan } from '@katsquare/types';
import { Button, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { PLAN_LABEL } from '../lib/plans';
import { useSession } from '../features/auth/useSession';
import {
  deleteWorkspace,
  getWorkspace,
  inviteMember,
  listInvites,
  listWorkspaceMembers,
  listWorkspaces,
  removeMember,
  renameWorkspace,
  revokeInvite,
  setMemberRole,
} from '../lib/api';

/** The two roles offered when inviting — the app's admin/reviewer split. */
const INVITE_ROLES: { value: MembershipRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'REVIEWER', label: 'Reviewer' },
];

// Local mirror of the per-plan seat caps + the manager/reviewer split. Kept out
// of the @katsquare/types value-import path on purpose: the web reads that package
// as a CJS barrel and rollup can't see newly-added named value exports through it
// (same gotcha as the local formatBytes). Server-side PLAN_LIMITS stays authoritative.
const SEAT_LIMITS: Record<WorkspacePlan, { managers: number | null; reviewers: number | null }> = {
  FREE: { managers: 1, reviewers: 2 },
  PRO: { managers: 1, reviewers: 5 },
  BUSINESS: { managers: 5, reviewers: 50 },
};
const isReviewerRole = (r: MembershipRole) => r === 'REVIEWER';
const ROLE_HINT: Record<MembershipRole, string> = {
  OWNER: 'Full control, incl. billing & deletion',
  ADMIN: 'Manage content, members & plan',
  MEMBER: 'Create and edit content',
  REVIEWER: 'Comment & approve only',
};

export function SettingsPage() {
  const { workspaceId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const ws = useQuery({ queryKey: ['workspace', workspaceId], queryFn: () => getWorkspace(workspaceId) });
  // All the user's workspaces, so a multi-workspace account can switch which
  // team they're managing without going back to the sidebar.
  const workspaces = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });
  const canManage = ws.data?.role === 'OWNER' || ws.data?.role === 'ADMIN';

  const [name, setName] = useState('');
  // The route element is reused when :workspaceId changes — re-sync the field so
  // workspace A's (possibly edited) name never carries over onto workspace B.
  // Keyed on the server name (not the query object) so a background refetch with
  // an unchanged name doesn't clobber an in-progress edit.
  const serverName = ws.data?.name;
  useEffect(() => {
    setName(serverName ?? '');
  }, [workspaceId, serverName]);
  const rename = useMutation({
    mutationFn: (n: string) => renameWorkspace(workspaceId, n),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast('Workspace renamed');
    },
    onError: (e: Error) => toast(e.message),
  });
  const del = useMutation({
    mutationFn: () => deleteWorkspace(workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/app');
    },
    onError: (e: Error) => toast(e.message),
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (ws.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  const w = ws.data!;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex flex-wrap items-center gap-3 border-b border-neutral-200 bg-white px-6 py-3.5">
        {(workspaces.data?.length ?? 0) > 1 ? (
          <label className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <span>Settings ·</span>
            <select
              value={workspaceId}
              onChange={(e) => navigate(`/workspaces/${e.target.value}/settings`)}
              className="max-w-[50vw] rounded-md border border-neutral-300 px-2 py-1 text-sm font-medium outline-none focus:border-neutral-900 sm:max-w-xs"
            >
              {workspaces.data!.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <h1 className="text-lg font-semibold tracking-tight">{w.name} · Settings</h1>
        )}
        <Link to="/app" className="ml-auto text-sm text-neutral-500 hover:text-neutral-900">
          ← Back
        </Link>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* General */}
          <Section title="General">
            <label className="mb-1 block text-xs font-medium text-neutral-500">Workspace name</label>
            <form
              className="flex items-center gap-2"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                const n = name.trim();
                if (n && n !== w.name) rename.mutate(n);
              }}
            >
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canManage}
                maxLength={120}
                className="flex-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm outline-none focus:border-neutral-900 disabled:bg-neutral-50"
              />
              {canManage && (
                <Button type="submit" disabled={rename.isPending} className="text-sm">
                  Save
                </Button>
              )}
            </form>
          </Section>

          {/* Plan */}
          <Section title="Plan">
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-neutral-900 px-3 py-1 text-xs font-semibold text-white">
                {PLAN_LABEL[w.plan]} plan
              </span>
              <Link to="/pricing" className="text-sm font-medium text-blue-600 hover:text-blue-700">
                {w.plan === 'BUSINESS' ? 'Compare plans' : 'Upgrade →'}
              </Link>
            </div>
          </Section>

          {/* Members */}
          <MembersSection workspaceId={workspaceId} canManage={canManage} plan={w.plan} />

          {/* Danger */}
          {canManage && (
            <Section title="Danger zone" danger>
              {confirmDelete ? (
                <div className="flex flex-col gap-3 rounded-lg bg-red-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm text-red-700">
                    Move this workspace and everything in it to the recycle bin?
                  </span>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="danger"
                      onClick={() => del.mutate()}
                      disabled={del.isPending}
                      className="text-sm"
                    >
                      {del.isPending ? <Spinner /> : 'Delete workspace'}
                    </Button>
                    <Button variant="ghost" className="text-sm" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-900">Delete this workspace</p>
                    <p className="text-xs text-neutral-500">Moves it and all its magazines to the recycle bin.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    className="shrink-0 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    Delete workspace
                  </button>
                </div>
              )}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, danger }: { title: string; children: React.ReactNode; danger?: boolean }) {
  return (
    <section className={`rounded-xl border bg-white p-5 ${danger ? 'border-red-200' : 'border-neutral-200'}`}>
      <h2 className={`mb-3 text-sm font-semibold ${danger ? 'text-red-700' : 'text-neutral-900'}`}>{title}</h2>
      {children}
    </section>
  );
}

function MembersSection({
  workspaceId,
  canManage,
  plan,
}: {
  workspaceId: string;
  canManage: boolean;
  plan: WorkspacePlan;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const members = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => listWorkspaceMembers(workspaceId),
  });
  const invites = useQuery({
    queryKey: ['invites', workspaceId],
    queryFn: () => listInvites(workspaceId),
    enabled: canManage,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
    queryClient.invalidateQueries({ queryKey: ['invites', workspaceId] });
  };

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MembershipRole>('ADMIN');
  const copyLink = (link: string) => {
    void navigator.clipboard?.writeText(link).catch(() => undefined);
    toast('Join link copied');
  };
  const invite = useMutation({
    mutationFn: () => inviteMember(workspaceId, { email: email.trim(), role }),
    onSuccess: (result) => {
      setEmail('');
      invalidate();
      if (result.status === 'added') {
        toast('Member added');
      } else {
        void navigator.clipboard?.writeText(result.link).catch(() => undefined);
        toast(`Invite sent to ${result.email} — join link copied`);
      }
    },
  });
  const changeRole = useMutation({
    mutationFn: (v: { id: string; role: MembershipRole }) => setMemberRole(workspaceId, v.id, v.role),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeMember(workspaceId, id),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvite(workspaceId, id),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message),
  });

  // Seat usage = current members + pending invites, split by category.
  const limits = SEAT_LIMITS[plan];
  const seatsUsed = (category: 'manager' | 'reviewer') => {
    const inCat = (r: MembershipRole) => (category === 'reviewer' ? isReviewerRole(r) : !isReviewerRole(r));
    return (
      (members.data?.filter((m) => inCat(m.role)).length ?? 0) +
      (invites.data?.filter((i) => inCat(i.role)).length ?? 0)
    );
  };
  const cap = (v: number | null) => (v == null ? '∞' : v);

  return (
    <Section title="Members">
      {canManage && (
        <>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (email.trim()) invite.mutate();
            }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@email.com"
              className="min-w-0 flex-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm outline-none focus:border-neutral-900"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as MembershipRole)}
              className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-900"
            >
              {INVITE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <Button type="submit" disabled={invite.isPending} className="text-sm">
              {invite.isPending ? <Spinner /> : 'Invite'}
            </Button>
          </form>
          <p className="mb-3 mt-2 text-xs text-neutral-400">
            Admins {seatsUsed('manager')}/{cap(limits.managers)} · Reviewers{' '}
            {seatsUsed('reviewer')}/{cap(limits.reviewers)}.{' '}
            <Link to="/pricing" className="text-blue-600 hover:text-blue-700">
              Need more?
            </Link>
          </p>
        </>
      )}
      {invite.isError && <p className="mb-2 text-xs text-red-500">{(invite.error as Error).message}</p>}

      {members.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {members.data!.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              isSelf={m.id === user?.id}
              canManage={canManage}
              onRole={(r) => changeRole.mutate({ id: m.id, role: r })}
              onRemove={() => remove.mutate(m.id)}
            />
          ))}
          {invites.data?.map((inv) => (
            <PendingInviteRow
              key={inv.id}
              invite={inv}
              canManage={canManage}
              onCopy={() => copyLink(inv.link)}
              onRevoke={() => revoke.mutate(inv.id)}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

/** A row for an invited email that hasn't accepted yet. */
function PendingInviteRow({
  invite,
  canManage,
  onCopy,
  onRevoke,
}: {
  invite: PendingInvite;
  canManage: boolean;
  onCopy: () => void;
  onRevoke: () => void;
}) {
  const roleLabel = isReviewerRole(invite.role) ? 'Reviewer' : 'Admin';
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-neutral-300 text-neutral-400">
        ✉
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-neutral-500">{invite.email}</p>
        <p className="text-xs text-amber-600">Invitation pending</p>
      </div>
      <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">{roleLabel}</span>
      {canManage && (
        <>
          <button
            type="button"
            onClick={onCopy}
            className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
          >
            Copy link
          </button>
          <button
            type="button"
            onClick={onRevoke}
            title="Revoke invitation"
            className="flex h-7 w-7 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
          >
            ✕
          </button>
        </>
      )}
    </li>
  );
}

function MemberRow({
  member,
  isSelf,
  canManage,
  onRole,
  onRemove,
}: {
  member: WorkspaceMember;
  isSelf: boolean;
  canManage: boolean;
  onRole: (role: MembershipRole) => void;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const initials = member.name
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">
          {member.name} {isSelf && <span className="text-xs text-neutral-400">(you)</span>}
        </p>
        <p className="truncate text-xs text-neutral-400">{member.email}</p>
      </div>
      {member.role === 'OWNER' ? (
        <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600" title={ROLE_HINT.OWNER}>
          Owner
        </span>
      ) : canManage && !isSelf ? (
        <select
          value={isReviewerRole(member.role) ? 'REVIEWER' : 'ADMIN'}
          onChange={(e) => onRole(e.target.value as MembershipRole)}
          className="rounded-md border border-neutral-200 px-2 py-1 text-xs text-neutral-600 outline-none focus:border-neutral-400"
        >
          {INVITE_ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      ) : (
        <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
          {isReviewerRole(member.role) ? 'Reviewer' : 'Admin'}
        </span>
      )}
      {canManage &&
        !isSelf &&
        (confirming ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-xs text-neutral-500">Remove {member.name.split(' ')[0]}?</span>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                onRemove();
              }}
              className="rounded px-1.5 py-0.5 text-xs font-semibold text-red-600 hover:bg-red-50"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded px-1 text-xs text-neutral-400 hover:text-neutral-700"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            title="Remove member"
            className="flex h-7 w-7 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
          >
            ✕
          </button>
        ))}
    </li>
  );
}
